/*
 * Manage JWT auth tokens.
 */
const JWTRegEx = /^[a-z0-9\-_]+?\.[a-z0-9\-_]+?\.([a-z0-9\-_]+)?$/i;

export function JWTDecode(packet = null, validated = false) {
    try {
        if (!validated && !isJWT(packet)) return false;

        let _packet = packet
            .replace(/-/g, '+').replace(/_/g, '/')
            .split(' ').pop().trim().split(/\./g),
            decode = (str) => decodeURIComponent(
                Array.prototype.map.call(window.atob(str), (c) =>
                    '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                ).join('')
            );

        return {
            header: JSON.parse(decode(_packet[0])),
            payload: JSON.parse(decode(_packet[1])),
            signature: _packet[2],
        };
    } catch {
        return false
    }
}

export function isJWT(packet) {
    return JWTRegEx.test(packet?.split?.(' ').pop?.().trim?.());
}

export class JWTManager {
    constructor(storage = window.sessionStorage, prefix = 'jwt') {
        this.storage = storage;
        this.prefix = prefix;
        this.regPrefix = new RegExp(`^${this.prefix}:`);
    }

    toKey(str) {
        return `${this.prefix}:${str}`;
    }

    hasPrefix(key) {
        return String(key).indexOf(`${this.prefix}:`) === 0;
    }

    stripPrefix(key) {
        return String(key).replace(this.regPrefix, '');
    }

    set(origin, packet) {
        if (isJWT(packet)) {
            this.storage.setItem(this.toKey(origin), JSON.stringify({
                packet,
                decoded: JWTDecode(packet, true),
            }));
        }

        return this;
    }

    get(origin, keyed = false, _default = undefined) {
        let data = this.storage.getItem(keyed ? origin : this.toKey(origin));

        try {
            return JSON.parse(data);
        } catch {
            return data || _default;
        }
    }

    has(origin, keyed = false) {
        return Object.prototype.hasOwnProperty.call(this.storage, keyed ? origin : this.toKey(origin));
    }

    remove(origin) {
        let key = this.toKey(origin);

        return this.has(key, true) && this.storage.removeItem(key) === undefined && !this.has(key, true);
    }

    clear() {
        Object.keys(this.storage).forEach(key => {
            if (this.hasPrefix(key)) this.storage.removeItem(key)
        });
    }

    keys() {
        return Object.keys(this.storage).reduce((acc, key) => this.hasPrefix(key) ? [
            ...acc,
            this.stripPrefix(key),
        ] : acc, []);
    }

    values() {
        return Object.keys(this.storage).reduce((acc, key) => this.hasPrefix(key) ? [
            ...acc,
            this.get(key, true),
        ] : acc, []);
    }

    entries() {
        return Object.keys(this.storage).reduce((acc, key) => this.hasPrefix(key) ? [
            ...acc,
            [
                this.stripPrefix(key),
                this.get(key, true),
            ],
        ] : acc, []);
    }

    object() {
        return Object.fromEntries(this.entries());
    }

    isAuthed(origin) {
        return isJWT(this.get(origin)?.packet);
    }

    get [Symbol.toStringTag]() {
        return 'JWTManager';
    }
}

// Export a JWTManager instance by default for fetch-utils.
export default new JWTManager();
